import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CheckCircle2, Receipt, Wallet, Trash2, Plus, X } from "lucide-react";
import { PAYMENT_MODES as MODES } from "@/lib/paymentModes";

/**
 * Default date for a disbursement of (year, month, period).
 *
 * Always the LAST day of the pay period so back-dated payroll lands in the
 * correct accounting month automatically. Field is editable if the user
 * wants to record the actual bank date.
 *
 * Periods:
 *   monthly       → last day of the month (e.g. 2026-02-28)
 *   first_half    → 15th of the month     (e.g. 2026-02-15)
 *   second_half   → last day of the month (e.g. 2026-02-28)
 */
function defaultDisbursementDate(year, month, period) {
  const pad = (n) => String(n).padStart(2, "0");
  const lastDay = new Date(year, month, 0).getDate();  // day 0 of next month = last day of this month
  const endDay = period === "first_half" ? 15 : lastDay;
  return `${year}-${pad(month)}-${pad(endDay)}`;
}

/**
 * MarkPaidDialog
 *
 * Records (or removes) a salary disbursement for a (branch, employee, year, month, period).
 * Upserted on the backend, so opening + saving an already-paid period replaces it.
 * The created row is auto-fed into the Accounting → Expenses page.
 */
export default function MarkPaidDialog({
  open, onOpenChange,
  branchId, employeeId, employeeName,
  year, month, period,
  netPay, currency,
  snapshot,                  // full preview blob to immortalize at save time
}) {
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    // Placeholder — the useEffect below replaces this with either the persisted
    // date (existing disbursement) OR defaultDisbursementDate() (new one).
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    mode: "cash",
    reference_no: "",
    disbursed_by: "",
    note: "",
  });
  // Payment splits — start with 1 row that mirrors the top-level Mode/Ref.
  // 2+ rows enables split-payment mode; sum(splits.amount) must equal form.amount.
  const [splits, setSplits] = useState([{ amount: "", mode: "cash", reference_no: "" }]);
  // Multi-branch salary attribution — only populated when the employee is a
  // branch_admin assigned to 2+ branches. When populated, a "Charge salary to"
  // picker is rendered inside the dialog with the current branch as default.
  const [salaryBranches, setSalaryBranches] = useState([]);
  const [expenseBranchId, setExpenseBranchId] = useState("");

  useEffect(() => {
    if (!open || !branchId || !employeeId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch existing disbursement + eligible salary branches in parallel
        const [{ data }, { data: sb }] = await Promise.all([
          api.get("/payslip/salary-disbursement", {
            params: { branch_id: branchId, employee_id: employeeId, year, month, period },
          }),
          api.get("/payslip/salary-branches", { params: { employee_id: employeeId } })
            .catch(() => ({ data: { branches: [] } })),
        ]);
        if (cancelled) return;
        const branches = (sb && sb.branches) || [];
        setSalaryBranches(branches);
        if (data) {
          setExisting(data);
          setForm({
            date: data.date || "",
            amount: String(data.amount || ""),
            mode: data.mode || "cash",
            reference_no: data.reference_no || "",
            disbursed_by: data.disbursed_by || "",
            note: data.note || "",
          });
          setExpenseBranchId(data.expense_branch_id || branchId);
          if (Array.isArray(data.splits) && data.splits.length >= 2) {
            setSplits(data.splits.map((s) => ({
              amount: String(s.amount ?? ""),
              mode: s.mode || "cash",
              reference_no: s.reference_no || "",
            })));
          } else {
            setSplits([{
              amount: String(data.amount || ""),
              mode: data.mode || "cash",
              reference_no: data.reference_no || "",
            }]);
          }
        } else {
          setExisting(null);
          const preset = netPay > 0 ? netPay.toFixed(2) : "";
          setForm((f) => ({
            ...f,
            // Default to period-end so back-dated payroll auto-lands in the
            // correct accounting month. Field remains fully editable.
            date: defaultDisbursementDate(year, month, period),
            amount: preset,
            mode: "cash",
            reference_no: "",
            disbursed_by: "",
            note: "",
          }));
          setSplits([{ amount: preset, mode: "cash", reference_no: "" }]);
          setExpenseBranchId(branchId);  // default to the current viewing branch
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, branchId, employeeId, year, month, period, netPay]);

  const isSplit = splits.length >= 2;

  // Keep the single-row split synced with the top-level Amount + Mode + Ref
  // so single-payment mode behaves exactly like before.
  useEffect(() => {
    if (splits.length === 1) {
      setSplits([{ amount: form.amount, mode: form.mode, reference_no: form.reference_no }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.amount, form.mode, form.reference_no]);

  const splitTotal = useMemo(
    () => splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0),
    [splits],
  );
  const requiredTotal = parseFloat(form.amount) || 0;
  const splitMismatch = isSplit && Math.abs(splitTotal - requiredTotal) > 0.01;

  const addSplit = () => {
    // New split defaults to the remaining amount so users can finish quickly.
    const remaining = Math.max(0, requiredTotal - splitTotal);
    setSplits((prev) => [
      ...prev,
      { amount: remaining > 0 ? remaining.toFixed(2) : "", mode: "online", reference_no: "" },
    ]);
  };
  const removeSplit = (idx) => {
    setSplits((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== idx);
      // Collapsing back to single-row → resync top-level fields from it.
      if (next.length === 1) {
        setForm((f) => ({
          ...f,
          amount: next[0].amount || f.amount,
          mode: next[0].mode || "cash",
          reference_no: next[0].reference_no || "",
        }));
      }
      return next;
    });
  };
  const updateSplit = (idx, patch) => {
    setSplits((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than zero"); return; }
    if (!form.date) { toast.error("Pick a date"); return; }
    if (isSplit) {
      for (const s of splits) {
        const sa = parseFloat(s.amount);
        if (!sa || sa <= 0) { toast.error("Each payment must have an amount greater than zero"); return; }
      }
      if (splitMismatch) {
        toast.error(`Split total (${splitTotal.toFixed(2)}) does not match Amount (${amt.toFixed(2)})`);
        return;
      }
    }
    setBusy(true);
    try {
      const primary = isSplit ? splits[0] : { mode: form.mode, reference_no: form.reference_no };
      const body = {
        branch_id: branchId,
        employee_id: employeeId,
        year, month, period,
        date: form.date,
        amount: amt,
        // Legacy top-level fields — set to the first split for readable summaries.
        mode: primary.mode || "cash",
        reference_no: (primary.reference_no || "").trim(),
        disbursed_by: form.disbursed_by.trim(),
        note: form.note.trim(),
        snapshot: snapshot || {},
      };
      // Send `expense_branch_id` only when the multi-branch admin has picked
      // a target and it differs from the current viewing branch.
      if (salaryBranches.length >= 2 && expenseBranchId && expenseBranchId !== branchId) {
        body.expense_branch_id = expenseBranchId;
      }
      if (isSplit) {
        body.splits = splits.map((s) => ({
          amount: parseFloat(s.amount) || 0,
          mode: s.mode || "cash",
          reference_no: (s.reference_no || "").trim(),
        }));
      }
      // Save & Mark as Paid: upsert salary disbursement AND snapshot the pay-slip
      // for future viewing (immutable history).
      await api.post("/payslip/save-and-mark-paid", body);
      toast.success(existing
        ? "Pay slip resaved · history snapshot recorded"
        : "Saved & marked as paid · history snapshot recorded");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/payslip/salary-disbursement", {
        params: { branch_id: branchId, employee_id: employeeId, year, month, period },
      });
      toast.success("Disbursement removed — Expenses row deleted");
      setConfirmDelete(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              {existing ? <Receipt className="w-5 h-5 text-brand-purple" /> : <CheckCircle2 className="w-5 h-5 text-[#2E7D32]" />}
              {existing ? "Edit Salary Payment" : "Save & Mark as Paid"}
            </DialogTitle>
            <DialogDescription>
              {existing
                ? "Update the salary disbursement record for this period. A new pay-slip snapshot will be saved to history. Changes flow through to Accounting → Expenses."
                : "Record the salary payment, snapshot the pay-slip for future viewing, and add the entry to Accounting → Expenses. The snapshot stays intact even if employee data changes later."}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-10 text-center text-brand-inkMuted font-heading">Loading...</div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-2xl bg-[#F1F5FF] border border-brand-purple/15 px-4 py-3 flex items-center gap-3 text-sm">
                <Wallet className="w-5 h-5 text-brand-purple shrink-0" />
                <div className="flex-1">
                  <p className="font-heading font-semibold text-foreground">{employeeName}</p>
                  <p className="text-xs text-brand-inkMuted">{prettyPeriod(year, month, period)} · Net Pay {currency}{Number(netPay || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="font-heading text-sm">Date <span className="text-[#C62828]">*</span></Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20 mt-1.5" data-testid="mark-paid-date" />
                </div>
                <div>
                  <Label className="font-heading text-sm">Amount <span className="text-[#C62828]">*</span></Label>
                  <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20 mt-1.5" data-testid="mark-paid-amount" />
                </div>
              </div>

              {/* Multi-branch admin: choose which branch's expenses this
                  salary lands in. Only rendered when the employee is a
                  branch_admin assigned to 2+ branches. */}
              {salaryBranches.length >= 2 && (
                <div className="rounded-2xl border border-brand-purple/30 bg-brand-purpleLight/20 p-3 space-y-1.5" data-testid="mark-paid-expense-branch-block">
                  <Label className="font-heading text-sm flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5 text-brand-purple" />
                    Charge salary to branch <span className="text-[#C62828]">*</span>
                  </Label>
                  <p className="text-[11px] text-brand-inkMuted -mt-0.5">
                    This admin oversees {salaryBranches.length} branches — pick which one this salary counts against in Expenses and Cash Analysis.
                  </p>
                  <select
                    value={expenseBranchId}
                    onChange={(e) => setExpenseBranchId(e.target.value)}
                    className="w-full h-11 rounded-2xl border border-brand-purple/40 bg-white px-3 text-sm"
                    data-testid="mark-paid-expense-branch"
                  >
                    {salaryBranches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}{b.id === branchId ? " (current)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Payments section — single row = single-payment; 2+ rows = split-payment */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-heading text-sm">
                    {isSplit ? `Payments (${splits.length} splits)` : "Payment"}
                  </Label>
                  {isSplit && (
                    <span
                      className={`text-xs font-heading tabular-nums ${splitMismatch ? "text-[#C62828]" : "text-[#2E7D32]"}`}
                      data-testid="mark-paid-split-total"
                    >
                      Total: {currency}{splitTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {splitMismatch && ` · off by ${currency}${Math.abs(splitTotal - requiredTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </span>
                  )}
                </div>

                {isSplit ? (
                  <div className="space-y-2">
                    {splits.map((s, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`mark-paid-split-row-${idx}`}>
                        <div className="col-span-3">
                          {idx === 0 && <Label className="text-[10px] font-heading uppercase tracking-wider text-brand-inkMuted">Amount</Label>}
                          <Input
                            type="number" step="0.01" min="0.01"
                            value={s.amount}
                            onChange={(e) => updateSplit(idx, { amount: e.target.value })}
                            className="rounded-2xl h-10 border-brand-purple/20 mt-1"
                            data-testid={`mark-paid-split-amount-${idx}`}
                          />
                        </div>
                        <div className="col-span-4">
                          {idx === 0 && <Label className="text-[10px] font-heading uppercase tracking-wider text-brand-inkMuted">Mode</Label>}
                          <select
                            value={s.mode}
                            onChange={(e) => updateSplit(idx, { mode: e.target.value })}
                            className="w-full h-10 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm mt-1"
                            data-testid={`mark-paid-split-mode-${idx}`}
                          >
                            {MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                          </select>
                        </div>
                        <div className="col-span-4">
                          {idx === 0 && <Label className="text-[10px] font-heading uppercase tracking-wider text-brand-inkMuted">Reference No.</Label>}
                          <Input
                            value={s.reference_no}
                            onChange={(e) => updateSplit(idx, { reference_no: e.target.value })}
                            placeholder="GCash / cheque #"
                            className="rounded-2xl h-10 border-brand-purple/20 mt-1"
                            data-testid={`mark-paid-split-reference-${idx}`}
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button
                            type="button" variant="ghost" size="sm"
                            onClick={() => removeSplit(idx)}
                            className="h-10 w-10 p-0 text-[#C62828] hover:bg-[#FFEBEE]"
                            data-testid={`mark-paid-split-remove-${idx}`}
                            title="Remove this payment"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="font-heading text-sm">Mode of Disbursement</Label>
                      <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm mt-1.5" data-testid="mark-paid-mode">
                        {MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                      </select>
                    </div>
                    <div>
                      <Label className="font-heading text-sm">Reference No.</Label>
                      <Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20 mt-1.5" placeholder="GCash txn / cheque #" data-testid="mark-paid-reference" />
                    </div>
                  </div>
                )}

                <Button
                  type="button" variant="outline" size="sm"
                  onClick={addSplit}
                  className="rounded-full border-brand-purple/40 text-brand-purple hover:bg-brand-purpleLight/40"
                  data-testid="mark-paid-add-payment"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Payment
                </Button>
              </div>

              <div>
                <Label className="font-heading text-sm">Disbursed By</Label>
                <Input value={form.disbursed_by} onChange={(e) => setForm({ ...form, disbursed_by: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20 mt-1.5" placeholder="Who released the payment" data-testid="mark-paid-disbursed-by" />
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2 justify-between sm:justify-between">
            <div>
              {existing && (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)} disabled={busy} className="rounded-full border-[#C62828]/40 text-[#C62828] hover:bg-[#FFEBEE]" data-testid="mark-paid-revoke-btn">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Revert to Unpaid
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-brand-purple/30">Cancel</Button>
              <Button type="button" onClick={save} disabled={busy || loading || splitMismatch} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="mark-paid-save-btn">
                {busy ? "Saving..." : existing ? "Save Changes" : "Save & Mark as Paid"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="mark-paid-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Revert to Unpaid?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the salary disbursement record. The Accounting → Expenses row will disappear as well.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="confirm-mark-paid-revoke">Revert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function prettyPeriod(year, month, period) {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mn = months[(month || 1) - 1];
  if (period === "first_half") return `${mn} ${year} · 1st half`;
  if (period === "second_half") return `${mn} ${year} · 2nd half`;
  return `${mn} ${year} · Monthly`;
}
